// What Brahms wrote for Ein deutsches Requiem, and the seating map's geometry.
// Ported from listen.datu.dev/w/brahms-requiem (listen repo, static/work.html, 2026-11-02),
// where the map was first built. Instrument lists from the Breitkopf complete edition (the
// list at the head of each movement). mv: the movements an instrument plays in. parts: how
// many separate parts he wrote; partsIn: where a movement has fewer (two horns only in 1, 4, 5).

export type Counts = Record<string, number>;

export const ALL = [1, 2, 3, 4, 5, 6, 7];
export const MV: Record<number, string> = {
  1: 'Selig sind, die da Leid tragen', 2: 'Denn alles Fleisch es ist wie Gras', 3: 'Herr, lehre doch mich',
  4: 'Wie lieblich sind deine Wohnungen', 5: 'Ihr habt nun Traurigkeit', 6: 'Denn wir haben hie keine bleibende Statt',
  7: 'Selig sind die Toten',
};
export const FAM: Record<string, { name: string; color: string }> = {
  str: { name: 'Strings', color: '#4f7a9a' }, ww: { name: 'Woodwinds', color: '#5b8c5a' },
  br: { name: 'Brass', color: '#b0731c' }, perc: { name: 'Timpani', color: '#8c5a7a' },
  harp: { name: 'Harp', color: '#6f63a6' },
};

export interface Section {
  id: string; name: string; ab: string; fam: string; mv: number[];
  parts?: number; partsIn?: Record<number, number>; note?: string;
}
export const S: Section[] = [
  { id: 'vn1', name: 'Violin I', ab: 'V1', fam: 'str', mv: [2, 3, 4, 5, 6, 7], note: 'no violins in 1' },
  { id: 'vn2', name: 'Violin II', ab: 'V2', fam: 'str', mv: [2, 3, 4, 5, 6, 7], note: 'no violins in 1' },
  { id: 'va', name: 'Viola', ab: 'Va', fam: 'str', mv: ALL, note: 'divided in 1' },
  { id: 'vc', name: 'Cello', ab: 'Vc', fam: 'str', mv: ALL, note: 'three parts in 1' },
  { id: 'cb', name: 'Bass', ab: 'Cb', fam: 'str', mv: ALL },
  { id: 'picc', name: 'Piccolo', ab: 'Pc', fam: 'ww', parts: 1, mv: [2, 6], note: 'plays with both flutes in 2 and 6' },
  { id: 'fl', name: 'Flute', ab: 'Fl', fam: 'ww', parts: 2, mv: ALL },
  { id: 'ob', name: 'Oboe', ab: 'Ob', fam: 'ww', parts: 2, mv: ALL },
  { id: 'cl', name: 'Clarinet', ab: 'Cl', fam: 'ww', parts: 2, mv: [2, 3, 4, 5, 6, 7], note: 'not in 1' },
  { id: 'bsn', name: 'Bassoon', ab: 'Bn', fam: 'ww', parts: 2, mv: ALL },
  { id: 'cbsn', name: 'Contrabassoon', ab: 'Cf', fam: 'ww', parts: 1, mv: [2, 3, 6, 7], note: 'ad lib.' },
  { id: 'hn', name: 'Horn', ab: 'Hn', fam: 'br', parts: 4, partsIn: { 1: 2, 4: 2, 5: 2 }, mv: ALL, note: 'four in 2, 3, 6, 7; two in 1, 4, 5' },
  { id: 'tpt', name: 'Trumpet', ab: 'Tp', fam: 'br', parts: 2, mv: [2, 3, 6] },
  { id: 'tbn', name: 'Trombone', ab: 'Tb', fam: 'br', parts: 3, mv: [1, 2, 3, 6, 7] },
  { id: 'tuba', name: 'Tuba', ab: 'Tu', fam: 'br', parts: 1, mv: [2, 3, 6], note: 'doubles the third trombone' },
  { id: 'timp', name: 'Timpani', ab: 'Ti', fam: 'perc', parts: 1, mv: [2, 3, 6] },
  { id: 'harp', name: 'Harp', ab: 'Hp', fam: 'harp', parts: 1, mv: [1, 2, 7], note: 'one part, "at least doubled"' },
];
export const BY: Record<string, Section> = Object.fromEntries(S.map(s => [s.id, s]));
export const zero = (): Counts => Object.fromEntries(S.map(s => [s.id, 0]));
const P = (o: Counts): Counts => Object.assign(zero(), o);
export const PRESETS: { name: string; counts: Counts }[] = [
  { name: 'Current plan (30)', counts: P({ vn1: 4, vn2: 4, va: 4, vc: 4, cb: 2, fl: 1, ob: 1, cl: 1, hn: 2, tpt: 2, tbn: 3, timp: 1, harp: 1 }) },
  { name: "Brahms's full forces", counts: P({ vn1: 14, vn2: 12, va: 10, vc: 8, cb: 6, picc: 1, fl: 2, ob: 2, cl: 2, bsn: 2, cbsn: 1, hn: 4, tpt: 2, tbn: 3, tuba: 1, timp: 1, harp: 2 }) },
  { name: 'Brahms winds, small strings (34)', counts: P({ vn1: 3, vn2: 3, va: 3, vc: 4, cb: 2, fl: 2, ob: 2, cl: 2, bsn: 2, hn: 4, tpt: 2, tbn: 3, timp: 1, harp: 1 }) },
  { name: 'Linckelmann plus brass (31)', counts: P({ vn1: 5, vn2: 4, va: 4, vc: 4, cb: 2, fl: 1, ob: 1, cl: 1, bsn: 1, hn: 1, tpt: 2, tbn: 3, timp: 1, harp: 1 }) },
  { name: 'Linckelmann as published', counts: P({ vn1: 4, vn2: 4, va: 3, vc: 3, cb: 2, fl: 1, ob: 1, cl: 1, bsn: 1, hn: 1, timp: 1 }) },
  { name: 'Brahms brass, three winds, fuller strings (33)', counts: P({ vn1: 6, vn2: 5, va: 4, vc: 4, cb: 2, fl: 1, ob: 1, cl: 1, hn: 2, tpt: 2, tbn: 3, timp: 1, harp: 1 }) },
];

export function partsFor(s: Section, m: number): number | null {
  if (!s.parts) return null;
  if (m && !s.mv.includes(m)) return 0;
  if (m && s.partsIn && s.partsIn[m] != null) return s.partsIn[m];
  return s.parts;
}
export function rests(s: Section, i: number, m: number): boolean {
  if (!m) return false;
  if (!s.mv.includes(m)) return true;
  return !!(s.partsIn && s.partsIn[m] != null && i >= s.partsIn[m]);
}
export const total = (c: Counts) => S.reduce((t, s) => t + (c[s.id] || 0), 0);

// ---------------------------------------------------------------- the seating
export const CX = 500, CY = 610;
const GAP = 30, DR = 34;
const rad = (d: number) => d * Math.PI / 180;
export const at = (r: number, deg: number): [number, number] => [CX + r * Math.cos(rad(deg)), CY - r * Math.sin(rad(deg))];
type Pt = [number, number, number];
// Fill an arc between two angles, row after row outward; n seats plus one dashed seat to add.
function arc(n: number, a0: number, a1: number, r0: number): Pt[] {
  const out: Pt[] = []; let r = r0;
  while (out.length < n + 1) {
    const cap = Math.max(1, Math.floor(rad(a0 - a1) * r / GAP)), step = (a0 - a1) / cap;
    for (let i = 0; i < cap && out.length < n + 1; i++) out.push([...at(r, a0 - step * (i + 0.5)), r]);
    r += DR;
  }
  return out;
}
// One row across the middle, sections side by side, each with its dashed seat at its end.
function row(counts: Counts, r: number, ids: string[]): Record<string, Pt[]> {
  const step = GAP / r * 180 / Math.PI, sep = 0.7;
  const slots = ids.reduce((t, id, k) => t + counts[id] + 1 + (k ? sep : 0), 0);
  let a = 90 + (slots - 1) * step / 2; const out: Record<string, Pt[]> = {};
  ids.forEach((id, k) => {
    if (k) a -= sep * step;
    out[id] = [];
    for (let i = 0; i <= counts[id]; i++) { out[id].push([...at(r, a), r]); a -= step; }
  });
  return out;
}
export function layout(counts: Counts): Record<string, Pt[]> {
  const L: Record<string, Pt[]> = {};
  L.vn1 = arc(counts.vn1, 178, 134, 130);
  L.vn2 = arc(counts.vn2, 131, 92, 130);
  L.va = arc(counts.va, 89, 52, 130);
  L.vc = arc(counts.vc, 49, 8, 130);
  const maxR = Math.max(...['vn1', 'vn2', 'va', 'vc'].flatMap(id => L[id].map(p => p[2])));
  const W = Math.max(maxR + 50, 230);
  L.cb = arc(counts.cb, 46, 8, Math.max(...L.vc.map(p => p[2])) + 42);
  L.harp = arc(counts.harp, 176, 150, W + 6);
  Object.assign(L, row(counts, W, ['picc', 'fl', 'ob']), row(counts, W + 40, ['cl', 'bsn', 'cbsn']),
    row(counts, W + 84, ['hn', 'tpt', 'tbn', 'tuba']), row(counts, W + 128, ['timp']));
  return L;
}

// ---------------------------------------------------------------- the score player's lines
// A line in the note data is one staff of Brahms's score, named as the engraving repo's
// instruments.json names it (German). Each is tied to a seat on the map (or to none: the
// choir and the organ have no seats there) and to a General MIDI sound.
export interface Instrument { key: string; label: string; section: string | null; patch: string }
export const INSTRUMENTS: Instrument[] = [
  { key: 'Piccolo', label: 'Piccolo', section: 'picc', patch: 'piccolo' },
  { key: 'Flute', label: 'Flute', section: 'fl', patch: 'flute' },
  { key: 'Oboe', label: 'Oboe', section: 'ob', patch: 'oboe' },
  { key: 'Clarinet', label: 'Clarinet', section: 'cl', patch: 'clarinet' },
  { key: 'Bassoon', label: 'Bassoon', section: 'bsn', patch: 'bassoon' },
  { key: 'Contrabassoon', label: 'Contrabassoon', section: 'cbsn', patch: 'bassoon' },
  { key: 'Horn', label: 'Horn', section: 'hn', patch: 'french_horn' },
  { key: 'Trumpet', label: 'Trumpet', section: 'tpt', patch: 'trumpet' },
  { key: 'Trombone', label: 'Trombone', section: 'tbn', patch: 'trombone' },
  { key: 'Tuba', label: 'Tuba', section: 'tuba', patch: 'tuba' },
  { key: 'Timpani', label: 'Timpani', section: 'timp', patch: 'timpani' },
  { key: 'Harp', label: 'Harp', section: 'harp', patch: 'orchestral_harp' },
  { key: 'Violin I', label: 'Violin I', section: 'vn1', patch: 'violin' },
  { key: 'Violin II', label: 'Violin II', section: 'vn2', patch: 'violin' },
  { key: 'Viola', label: 'Viola', section: 'va', patch: 'viola' },
  { key: 'Cello', label: 'Cello', section: 'vc', patch: 'cello' },
  { key: 'Bass', label: 'Bass', section: 'cb', patch: 'contrabass' },
  { key: 'Choir', label: 'Choir', section: null, patch: 'choir_aahs' },
  { key: 'Organ', label: 'Organ', section: null, patch: 'church_organ' },
  { key: 'Piano', label: 'Piano', section: null, patch: 'acoustic_grand_piano' },
];
export const INSTR: Record<string, Instrument> = Object.fromEntries(INSTRUMENTS.map(i => [i.key, i]));
const LINE_RULES: [RegExp, string][] = [
  [/piccolo|kleine fl/i, 'Piccolo'], [/kontrafag|contrabassoon/i, 'Contrabassoon'],
  [/fl[öo]t|fløyt|flute/i, 'Flute'], [/oboe|obo/i, 'Oboe'], [/klarinet|clarinet/i, 'Clarinet'],
  [/fagot|bassoon/i, 'Bassoon'], [/h[öo]rn|valthorn/i, 'Horn'], [/trompet|trumpet/i, 'Trumpet'],
  [/posaun|trombon/i, 'Trombone'], [/tuba/i, 'Tuba'], [/pauke|timpan/i, 'Timpani'], [/harf|harp/i, 'Harp'],
  [/violinen ii|violin ii|fiolin ii/i, 'Violin II'], [/violinen i|violin i|fiolin i/i, 'Violin I'],
  [/bratsch|viola/i, 'Viola'], [/cell[oi]|violoncel/i, 'Cello'], [/kontrab|contrabass|double bass/i, 'Bass'],
  [/sopran|^alt$|^alto|tenor|^ba(ß|ss)$|chor/i, 'Choir'], [/orgel|organ/i, 'Organ'], [/klavier|piano/i, 'Piano'],
];
export function instrumentForName(name: string): Instrument | null {
  for (const [re, key] of LINE_RULES) if (re.test(name)) return INSTR[key];
  return null;
}
